/**
 * Travel Time Cache
 * Caches travel time calculations to reduce API calls
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import type { TravelMode } from '@/services/navigation';

const CACHE_KEY = '@tripflow_travel_times';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

type CachedTravelTime = {
  fromLocation: string;
  toLocation: string;
  mode: TravelMode;
  travelTime: number; // in minutes
  distance?: number; // in meters
  cachedAt: number; // timestamp
  departureTime?: number; // timestamp for traffic-aware routing
};

type TravelTimeCache = {
  [key: string]: CachedTravelTime;
};

/**
 * Generate cache key from locations and mode
 */
const generateCacheKey = (
  fromLocation: string,
  toLocation: string,
  mode: TravelMode,
  departureTime?: Date,
  context?: string
): string => {
  const normalizedFrom = fromLocation.toLowerCase().trim();
  const normalizedTo = toLocation.toLowerCase().trim();
  const timeKey = departureTime ? Math.floor(departureTime.getTime() / (60 * 60 * 1000)) : ''; // Hour-level granularity for traffic
  const contextKey = context ? context.toLowerCase().trim() : '';
  return `${normalizedFrom}|${normalizedTo}|${mode}|${timeKey}|${contextKey}`;
};

/**
 * Get cached travel time
 */
export const getCachedTravelTime = async (
  fromLocation: string,
  toLocation: string,
  mode: TravelMode,
  departureTime?: Date,
  context?: string
): Promise<number | null> => {
  try {
    const cacheData = await AsyncStorage.getItem(CACHE_KEY);
    if (!cacheData) return null;

    const cache: TravelTimeCache = JSON.parse(cacheData);
    const key = generateCacheKey(fromLocation, toLocation, mode, departureTime, context);
    const cached = cache[key];

    if (!cached) return null;

    // Check if cache is expired
    const age = Date.now() - cached.cachedAt;
    if (age > CACHE_EXPIRY_MS) {
      // Remove expired entry
      delete cache[key];
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      return null;
    }

    return cached.travelTime;
  } catch (error) {
    console.warn('[TravelTimeCache] Error getting cached travel time:', error);
    return null;
  }
};

/**
 * Cache travel time
 */
export const cacheTravelTime = async (
  fromLocation: string,
  toLocation: string,
  mode: TravelMode,
  travelTime: number,
  distance?: number,
  departureTime?: Date,
  context?: string
): Promise<void> => {
  try {
    const cacheData = await AsyncStorage.getItem(CACHE_KEY);
    const cache: TravelTimeCache = cacheData ? JSON.parse(cacheData) : {};

    const key = generateCacheKey(fromLocation, toLocation, mode, departureTime, context);
    cache[key] = {
      fromLocation,
      toLocation,
      mode,
      travelTime,
      distance,
      cachedAt: Date.now(),
      departureTime: departureTime?.getTime(),
    };

    // Limit cache size to 1000 entries
    const entries = Object.keys(cache);
    if (entries.length > 1000) {
      // Remove oldest 200 entries
      const sorted = entries.sort((a, b) => cache[a].cachedAt - cache[b].cachedAt);
      sorted.slice(0, 200).forEach(k => delete cache[k]);
    }

    await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('[TravelTimeCache] Error caching travel time:', error);
  }
};

/**
 * Clear travel time cache
 */
export const clearTravelTimeCache = async (): Promise<void> => {
  try {
    await AsyncStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn('[TravelTimeCache] Error clearing cache:', error);
  }
};

